import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexStroke,
    ApexTitleSubtitle,
    ApexXAxis,
} from "ng-apexcharts";
import { combineLatest, EMPTY, Observable, Subject } from "rxjs";
import { catchError, map, shareReplay, startWith, switchMap, takeUntil } from "rxjs/operators";
import { GetMobileStatsRequest, MobileApi, InstallationHistoryItem } from "../api/proxies";
import { StatsService } from "../services/stats.service";
import { NotifyService } from "@ui/notify";

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
};

@Component({
    selector: "installation-history",
    templateUrl: "./installation-history.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationHistoryComponent implements OnInit, OnDestroy {
    private _destroy$ = new Subject<void>();

    loading$!: Observable<boolean>;

    total$!: Observable<number>;

    chartOptions$!: Observable<Partial<ChartOptions>>;

    constructor(
        private statsService: StatsService,
        private mobileApi: MobileApi,
        private _notifyService: NotifyService
    ) {}

    ngOnInit(): void {
        const data$ = combineLatest([this.statsService.period$, this.statsService._tenant$]).pipe(
            takeUntil(this._destroy$),
            switchMap(([period, tenant]) => {
                const req = new GetMobileStatsRequest();
                req.start = period.periodStart;
                req.end = period.periodEnd;
                req.tenantId = tenant?.id;

                return this.mobileApi.installationHistory(req).pipe(
                    catchError((err) => {
                        this._notifyService.error("Falha ao carregar histórico de instalações");
                        return EMPTY;
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.loading$ = data$.pipe(
            map(() => false),
            startWith(true),
            catchError(() => EMPTY)
        );

        this.total$ = data$.pipe(
            map((items: InstallationHistoryItem[]) => items.reduce((acc, cur) => acc + cur.google + cur.apple, 0))
        );

        this.chartOptions$ = data$.pipe(
            map((items: InstallationHistoryItem[]) => {
                const series: ApexAxisChartSeries = [
                    { name: "Android", data: items.map((i) => i.google) },
                    { name: "iOS", data: items.map((i) => i.apple) },
                ];
                return {
                    series,
                    chart: { height: 350, type: "bar", toolbar: { show: false } },
                    dataLabels: { enabled: true },
                    stroke: { curve: "smooth" },
                    xaxis: {
                        type: "datetime",
                        categories: items.map((i) => i.date.toLocaleDateString()),
                    },
                } as Partial<ChartOptions>;
            })
        );
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
